import React, { RefObject, useCallback, useRef } from 'react';

import { SCROLL_CONFIG, ScrollPriority, useScrollActionManager } from './useScrollActionManager';

type UseChatScrollManagerProps = {
  chatContainerRef: RefObject<HTMLDivElement>;
  chatRefs?: React.MutableRefObject<Record<number, HTMLDivElement | null>>;
  hasUserScrolledUpRef?: React.MutableRefObject<boolean>;
};

export const useChatScrollManager = ({
  chatContainerRef,
  chatRefs,
  hasUserScrolledUpRef,
}: UseChatScrollManagerProps) => {
  const wasAtBottom = useRef(false);
  const firstVisibleChatId = useRef<number | null>(null);
  const isRestoringScroll = useRef(false);

  const scrollManager = useScrollActionManager();

  const isNearBottom = useCallback((): boolean => {
    const el = chatContainerRef.current;
    if (!el) {
      return false;
    }

    const threshold = SCROLL_CONFIG.thresholds.nearBottom;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distance <= threshold;

    return nearBottom;
  }, [chatContainerRef]);

  const scrollToBottom = useCallback(() => {
    const el = chatContainerRef.current;
    if (!el) {
      return;
    }

    if (hasUserScrolledUpRef?.current) {
      return;
    }

    scrollManager.addScrollAction(
      async () => {
        const targetScrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
        el.scrollTop = targetScrollTop;

        return new Promise<void>((resolve) => {
          let attempts = 0;
          const verifyScroll = () => {
            attempts++;
            const currentTarget = Math.max(0, el.scrollHeight - el.clientHeight);

            if (
              Math.abs(el.scrollTop - currentTarget) > 1 &&
              attempts < SCROLL_CONFIG.limits.maxVerificationAttempts
            ) {
              el.scrollTop = currentTarget;
              requestAnimationFrame(verifyScroll);
            } else {
              resolve();
            }
          };

          requestAnimationFrame(verifyScroll);
        });
      },
      ScrollPriority.GENERAL_AUTO_SCROLL,
      { description: 'scrollToBottom' },
    );
  }, [chatContainerRef, scrollManager, hasUserScrolledUpRef]);

  const rememberScrollHeight = useCallback(() => {
    const el = chatContainerRef.current;
    if (el && !isRestoringScroll.current && chatRefs) {
      const containerRect = el.getBoundingClientRect();

      for (const [chatId, chatEl] of Object.entries(chatRefs.current)) {
        if (chatEl) {
          const rect = chatEl.getBoundingClientRect();

          if (rect.top >= containerRect.top && rect.top <= containerRect.bottom) {
            firstVisibleChatId.current = parseInt(chatId, 10);
            break;
          }
        }
      }
    }
  }, [chatContainerRef, chatRefs]);

  const restoreScrollHeight = useCallback(() => {
    const el = chatContainerRef.current;
    if (el && firstVisibleChatId.current && !isRestoringScroll.current && chatRefs) {
      scrollManager.addScrollAction(
        async () => {
          isRestoringScroll.current = true;

          return new Promise<void>((resolve) => {
            let attempts = 0;
            const maxAttempts = SCROLL_CONFIG.limits.maxRestoreAttempts;

            const tryRestore = () => {
              attempts++;

              const targetElement = chatRefs.current[firstVisibleChatId.current!];

              if (targetElement) {
                targetElement.scrollIntoView({
                  block: 'start',
                  behavior: 'auto',
                });

                firstVisibleChatId.current = null;
                isRestoringScroll.current = false;
                resolve();
              } else if (attempts < maxAttempts) {
                requestAnimationFrame(tryRestore);
              } else {
                firstVisibleChatId.current = null;
                isRestoringScroll.current = false;
                resolve();
              }
            };

            setTimeout(tryRestore, 0);
          });
        },
        ScrollPriority.INFINITE_RESTORE,
        { description: 'restoreScrollHeight' },
      );
    }
  }, [chatContainerRef, chatRefs, scrollManager]);

  const updateWasAtBottom = useCallback(() => {
    wasAtBottom.current = isNearBottom();
  }, [isNearBottom]);

  return {
    isNearBottom,
    wasAtBottom,
    updateWasAtBottom,
    scrollToBottom,
    rememberScrollHeight,
    restoreScrollHeight,
    scrollManager,
  };
};
